import { staleTimes, useQueries } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { useEffect, useRef } from 'react';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import { AWAITING_CARRIER, CARRIER_FOLLOW_INTERVAL_MS, type Order } from './orders.ts';

export type HandOffProgress = {
  readonly awaiting: readonly Order[];
  readonly handedOver: readonly Order[];
  readonly refusedByCarrier: readonly Order[];
};

const isAwaiting = (order: Order): boolean => order.status === AWAITING_CARRIER;

const isRefusedByCarrier = (order: Order): boolean => !isAwaiting(order) && order.dispatch.failure !== null;

export const handOffProgressOf = (orders: readonly Order[]): HandOffProgress => ({
  awaiting: orders.filter(isAwaiting),
  handedOver: orders.filter((order) => !isAwaiting(order) && !isRefusedByCarrier(order)),
  refusedByCarrier: orders.filter(isRefusedByCarrier),
});

export const useHandOffProgress = (api: TypedClient, handedOff: readonly Order[]): HandOffProgress => {
  const { tenantId, invalidate } = usePageContext();
  const results = useQueries({
    queries: handedOff.map((order) => ({
      queryKey: queryKeys.order(tenantId, order.id),
      queryFn: () => api.get('/admin/orders/{id}', { path: { id: order.id } }),
      staleTime: staleTimes.orders,
      refetchInterval: (query: { state: { data: { order: Order } | undefined } }) =>
        isAwaiting(query.state.data?.order ?? order) ? CARRIER_FOLLOW_INTERVAL_MS : false,
    })),
  });
  const progress = handOffProgressOf(handedOff.map((order, index) => results[index]?.data?.order ?? order));
  const awaitingCount = progress.awaiting.length;
  const previousAwaiting = useRef(awaitingCount);
  useEffect(() => {
    if (awaitingCount < previousAwaiting.current) void invalidate('orders');
    previousAwaiting.current = awaitingCount;
  }, [awaitingCount, invalidate]);
  return progress;
};
